'use client';

import {
  fetchMoreProducts,
  type InfiniteScrollParams,
} from '@lib/data/products';
import { toProductCardVM } from '@lib/util/to-product-card-vm';
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import type { ProductCardVM } from 'types/view-models';

const PAGE_SIZE = 12;

export type UseInfiniteProductsParams = {
  mode: 'client' | 'server';
  totalCount: number;
  initialCount: number;
  fetchParams?: Omit<InfiniteScrollParams, 'offset' | 'limit'>;
};

export function useInfiniteProducts({
  mode,
  totalCount,
  initialCount,
  fetchParams,
}: UseInfiniteProductsParams) {
  const [extraProducts, setExtraProducts] = useState<ProductCardVM[]>([]);
  const [hasMore, setHasMore] = useState(
    mode === 'server' ? totalCount > initialCount : false,
  );
  const [isPending, startTransition] = useTransition();
  const loadingRef = useRef(false);
  const queryVersionRef = useRef(0);

  const paramsKey = JSON.stringify(fetchParams);

  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey is a serialised query identity — resets when query changes even if totalCount/initialCount are unchanged
  useEffect(() => {
    queryVersionRef.current += 1;
    setExtraProducts([]);
    setHasMore(mode === 'server' ? totalCount > initialCount : false);
    loadingRef.current = false;
  }, [totalCount, mode, initialCount, paramsKey]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || isPending || mode !== 'server') {
      return;
    }
    loadingRef.current = true;
    const offset = initialCount + extraProducts.length;
    const version = queryVersionRef.current;
    startTransition(async () => {
      try {
        const result = await fetchMoreProducts({
          ...fetchParams,
          offset,
          limit: PAGE_SIZE,
        } as Parameters<typeof fetchMoreProducts>[0]);
        if (queryVersionRef.current !== version) {
          return;
        }
        setExtraProducts((prev) => [
          ...prev,
          ...result.products.map(toProductCardVM),
        ]);
        setHasMore(result.hasMore);
      } finally {
        loadingRef.current = false;
      }
    });
  }, [mode, isPending, extraProducts.length, initialCount, fetchParams]);

  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  return { extraProducts, hasMore, isPending, loadMoreRef };
}
